import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  DialogActions,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  ListItemText,
  Popover,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useGetExtractCachedEnergySavingQuery } from '../../../api/energy-savings/extractCachedEnergySavingsApiSlice';
import {
  useGetBuildingsByAccountQuery,
  useUpdateBuildingMutation,
} from '../../../api/recalc/endpoints/buildingApiSlice';
import { useLazyGetDataQuery } from '../../../api/recalc/endpoints/dataApiSlice';
import {
  useGetBaselineDaysByJobSubscriptionIdQuery,
  useGetControlPeriodsByJobSubscriptionIdQuery,
  useGetSubscriptionsByAccountAndBuildingQuery,
} from '../../../api/recalc/endpoints/jobSubscriptionApiSlice';
import { useLazyGetUnitByIdQuery } from '../../../api/recalc/endpoints/unitsApiSlice';
import { getSelectedAccount } from '../../../redux/selectedAccountSlice';

import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../redux/snackbarSlice';
import HtmlTooltip from '@/components/tooltip/HtmlTooltip';

import EnergyConsumptionChart from '@/features/building-optimization-savings-dashboard/components/EnergyConsumptionChart';
import HorizontalBarChart from '@/features/building-optimization-savings-dashboard/components/HorizontalBarChart';
import SavingsSummaryCards from '@/features/building-optimization-savings-dashboard/components/SavingsSummaryCards';

const getIconForBillingMeterType = (billingMeterType) => {
  switch (billingMeterType) {
    case 'GasMeter':
      return <LocalFireDepartmentRoundedIcon />;
    case 'ElectricityMeter':
      return <BoltRoundedIcon />;
    case 'HeatMeter':
      return <MenuRoundedIcon sx={{ transform: 'rotate(90deg)', mb: 2 }} />;
    default:
      return null;
  }
};

const formatEnergyUnit = (value) => {
  if (Math.abs(value) >= 1000000) {
    return (value / 1000000).toFixed(1) + ' GWh';
  } else if (Math.abs(value) >= 1000) {
    return (value / 1000).toFixed(1) + ' MWh';
  } else {
    return value.toFixed(0) + ' kWh';
  }
};

const formatCO2Unit = (value) => {
  if (Math.abs(value) >= 1000000) {
    return (value / 1000000).toFixed(1) + ' t CO2e';
  } else if (Math.abs(value) >= 1000) {
    return (value / 1000).toFixed(1) + ' kg CO2e';
  } else {
    return value.toFixed(0) + ' g CO2e';
  }
};

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`vertical-tabpanel-${index}`}
    aria-labelledby={`vertical-tab-${index}`}
    style={{ width: '100%' }}
    {...other}
  >
    {value === index && (
      <Box sx={{ width: '100%', display: 'flex' }}>
        <Typography sx={{ flex: 1 }}>{children}</Typography>
      </Box>
    )}
  </div>
);

function formatAndEncodeTimestampForAPI(date) {
  const isoString = DateTime.fromJSDate(date, { zone: 'utc' }).toFormat(
    "yyyy-MM-dd'T'HH:mm:ss'Z'",
  );
  return encodeURIComponent(isoString);
}

const getMeterSavings = (billingMeterId, energySavings) => {
  // Return null if energySavings or BuildingEnergySavings is missing or empty
  if (
    !energySavings ||
    !energySavings.BuildingEnergySavings ||
    energySavings.BuildingEnergySavings.length === 0
  ) {
    return null;
  }

  // Initialize variables to store both energy and CO2 savings
  let energySavingsResult = null;
  let co2SavingsResult = null;

  // Iterate through each building in BuildingEnergySavings
  energySavings.BuildingEnergySavings.forEach((building) => {
    // Check for energy savings
    const energySavingsFound = [
      ...(building.HeatMeterSavings || []),
      ...(building.GasMeterSavings || []),
      ...(building.ElectricityMeterSavings || []),
    ].find((saving) => saving.BillingMeterId === billingMeterId);

    if (energySavingsFound) {
      energySavingsResult = energySavingsFound;
    }

    // Check for CO2 savings
    const co2SavingsFound = [
      ...(building.HeatCO2Savings || []),
      ...(building.GasCO2Savings || []),
      ...(building.ElectricityCO2Savings || []),
    ].find((saving) => saving.BillingMeterId === billingMeterId);

    if (co2SavingsFound) {
      co2SavingsResult = co2SavingsFound;
    }
  });

  // Combine the energy and CO2 savings if both exist, otherwise return what we have
  if (energySavingsResult || co2SavingsResult) {
    return {
      ...energySavingsResult,
      CO2Savings: co2SavingsResult || null, // Attach CO2 savings if they exist
    };
  }

  return null;
};

const initializeTimeRange = () => {
  const today = DateTime.now(); // Current date
  const currentYear = today.year;
  const currentMonth = today.month;

  let startOfSeason;
  let endOfSeason;

  // Define the current or upcoming season
  if (currentMonth >= 10) {
    startOfSeason = DateTime.fromObject({
      year: currentYear,
      month: 10,
      day: 1,
    });
    endOfSeason = DateTime.fromObject({
      year: currentYear + 1,
      month: 5,
      day: 31,
    });
  } else if (currentMonth <= 5) {
    startOfSeason = DateTime.fromObject({
      year: currentYear - 1,
      month: 10,
      day: 1,
    });
    endOfSeason = DateTime.fromObject({ year: currentYear, month: 5, day: 31 });
  } else {
    startOfSeason = DateTime.fromObject({
      year: currentYear - 1,
      month: 10,
      day: 1,
    });
    endOfSeason = DateTime.fromObject({ year: currentYear, month: 5, day: 31 });
  }

  // Check if today falls within the current season
  const isTodayInSeason = today >= startOfSeason && today <= endOfSeason;

  // If today is not in season, calculate the days until the next season
  let daysUntilNextSeason = 0;
  if (!isTodayInSeason) {
    const nextSeasonStart = DateTime.fromObject({
      year: currentYear,
      month: 10,
      day: 1,
    });
    daysUntilNextSeason = nextSeasonStart.diff(today, 'days').days;
  }

  return {
    start: startOfSeason.toMillis(),
    end: endOfSeason.toMillis(),
    isTodayInSeason,
    daysUntilNextSeason, // Return the days until next season
  };
};

const PortalPage = () => {
  const selectedAccount = useSelector(getSelectedAccount);
  const { start, end, isTodayInSeason, daysUntilNextSeason } =
    initializeTimeRange();
  const {
    data: buildings,
    isLoading: isLoadingBuildings,
    isSuccess: isSuccessBuilding,
    isFetching: isFetchingBuildings,
  } = useGetBuildingsByAccountQuery(selectedAccount.AccountId, {
    skip: !selectedAccount.AccountId,
  });

  const buildingIds =
    (isSuccessBuilding && buildings?.map((building) => building.BuildingId)) ||
    [];

  const {
    data: energySavings,
    isLoading: isLoadingEnergySavings,
    isFetching: isFetchingEnergySavings,
  } = useGetExtractCachedEnergySavingQuery(
    { buildingsIdArray: buildingIds },
    { skip: !selectedAccount.AccountId || buildingIds.length === 0 },
  );

  const [updateBuilding, { isLoading: isUpdatingBuilding }] =
    useUpdateBuildingMutation();
  const [
    getUnitById,
    { isLoading: isLoadingUnitById, isFetching: isFetchingUnitById },
  ] = useLazyGetUnitByIdQuery();
  const [getData, { isLoading: isLoadingData, isFetching: isFetchingData }] =
    useLazyGetDataQuery();

  const dispatch = useDispatch();

  const [tabValue, setTabValue] = useState(0);
  const [mergedBillingMeters, setMergedBillingMeters] = useState(null);
  const [selectedBillingMeter, setSelectedBillingMeter] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [billingMeterData, setBillingMeterData] = useState(null);
  const [selectedMeterSavings, setSelectedMeterSavings] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [newValue, setNewValue] = useState('');
  const [newStartTime, setNewStartTime] = useState(null);
  const [newEndTime, setNewEndTime] = useState(null);
  const [error, setError] = useState('');

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setError('');
  };

  const open = Boolean(anchorEl);

  // Fetch subscriptions
  const {
    data: subscriptions,
    isLoading: isLoadingSubscriptions,
    isFetching: isFetchingSubscriptions,
  } = useGetSubscriptionsByAccountAndBuildingQuery(
    {
      accountId: selectedAccount?.AccountId,
      buildingId: selectedBillingMeter?.BuildingId,
    },
    { skip: !selectedBillingMeter },
  );

  // Check if subscriptions are loaded and non-empty before making the next queries
  const hasSubscriptions = subscriptions && subscriptions.length > 0;

  const firstActiveSubscription = subscriptions?.find(
    (subscription) => subscription.StateCode === 'Active',
  );

  // Fetch baseline days only when subscriptions are available
  const {
    data: baselineDays,
    isLoading: isLoadingBaselineDays,
    isFetching: isFetchingBaselineDays,
  } = useGetBaselineDaysByJobSubscriptionIdQuery(
    {
      jobSubscriptionId: firstActiveSubscription?.JobSubscriptionId,
      timestamp: {
        startDate: formatAndEncodeTimestampForAPI(new Date(start)),
        endDate: formatAndEncodeTimestampForAPI(new Date(end)),
      },
    },
    { skip: !hasSubscriptions || !firstActiveSubscription },
  );

  // Fetch control periods only when subscriptions are available
  const {
    data: controlPeriods,
    isLoading: isLoadingControlPeriods,
    isFetching: isFetchingControlPeriods,
  } = useGetControlPeriodsByJobSubscriptionIdQuery(
    {
      jobSubscriptionId: firstActiveSubscription?.JobSubscriptionId,
      timestamp: formatAndEncodeTimestampForAPI(new Date(start)),
    },
    { skip: !hasSubscriptions },
  );

  const fetchDataAndAggregate = async (billingMeter) => {
    if (!billingMeter) return;
    try {
      const data = await getData({
        from: formatAndEncodeTimestampForAPI(new Date(start)),
        to: formatAndEncodeTimestampForAPI(new Date(end)),
        unitId: billingMeter.UnitId,
        aggregateType: 'Hour',
        datatype: billingMeter.DataType,
        unitTypeInputId: billingMeter.UnitTypeInputId,
      }).unwrap();

      setBillingMeterData(data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  useEffect(() => {
    // Get the current CO2 coefficient for the selected meter
    const currentCO2Coefficient = getCO2CoefficientForSelectedMeter();

    // If there's a valid CO2 coefficient, populate the state with its values
    if (currentCO2Coefficient !== null) {
      setNewValue(currentCO2Coefficient);

      // Find the specific CO2 coefficient object to extract start and end times
      const coefficientArray =
        selectedBuilding?.UserInput?.CO2Coefficients?.[
          selectedBillingMeter.BillingMeterType === 'HeatMeter'
            ? 'Heat'
            : selectedBillingMeter.BillingMeterType === 'GasMeter'
              ? 'Gas'
              : 'Electricity'
        ] || [];

      const validCoefficient = coefficientArray.find((item) => {
        const start = DateTime.fromISO(item.timestamp_start);
        const end = DateTime.fromISO(item.timestamp_end);
        return start <= DateTime.now() && end >= DateTime.now();
      });

      // Set start and end times if found, formatted for 'datetime-local' inputs
      if (validCoefficient) {
        setNewStartTime(
          DateTime.fromISO(validCoefficient.timestamp_start).toFormat(
            "yyyy-MM-dd'T'HH:mm",
          ),
        );
        setNewEndTime(
          DateTime.fromISO(validCoefficient.timestamp_end).toFormat(
            "yyyy-MM-dd'T'HH:mm",
          ),
        );
      }
    } else {
      // If no valid coefficient, reset the state
      setNewValue('');
      setNewStartTime(null);
      setNewEndTime(null);
    }

    setError(''); // Optionally reset error state
  }, [selectedBuilding, selectedBillingMeter]);

  const isLoading = useMemo(
    () =>
      isLoadingBuildings ||
      isFetchingBuildings ||
      isLoadingEnergySavings ||
      isFetchingEnergySavings ||
      isLoadingUnitById ||
      isFetchingUnitById ||
      isLoadingData ||
      isFetchingData ||
      isLoadingSubscriptions ||
      isFetchingSubscriptions ||
      isLoadingBaselineDays ||
      isFetchingBaselineDays ||
      isLoadingControlPeriods ||
      isFetchingControlPeriods[
        (isLoadingBuildings,
        isFetchingBuildings,
        isLoadingEnergySavings,
        isFetchingEnergySavings,
        isLoadingUnitById,
        isFetchingUnitById,
        isLoadingData,
        isFetchingData,
        isLoadingSubscriptions,
        isFetchingSubscriptions,
        isLoadingBaselineDays,
        isFetchingBaselineDays,
        isLoadingControlPeriods,
        isFetchingControlPeriods)
      ],
  );

  // Determine the current loading message
  const loadingMessage = useMemo(() => {
    if (isLoadingBuildings || isFetchingBuildings)
      return 'Loading buildings...';
    if (isLoadingEnergySavings || isFetchingEnergySavings)
      return 'Loading energy savings...';
    if (isLoadingUnitById || isFetchingUnitById)
      return 'Loading unit details...';
    if (isLoadingData || isFetchingData) return 'Loading data...';
    if (isLoadingSubscriptions || isFetchingSubscriptions)
      return 'Loading subscriptions...';
    if (isLoadingBaselineDays || isFetchingBaselineDays)
      return 'Loading baseline days...';
    if (isLoadingControlPeriods || isFetchingControlPeriods)
      return 'Loading control periods...';
    return null;
  }, [
    isLoadingBuildings,
    isFetchingBuildings,
    isLoadingEnergySavings,
    isFetchingEnergySavings,
    isLoadingUnitById,
    isFetchingUnitById,
    isLoadingData,
    isFetchingData,
    isLoadingSubscriptions,
    isFetchingSubscriptions,
    isLoadingBaselineDays,
    isFetchingBaselineDays,
    isLoadingControlPeriods,
    isFetchingControlPeriods,
  ]);

  const fetchUnits = async (unitIds, billingMeters) => {
    if (unitIds.length > 0) {
      const units = await Promise.all(
        unitIds.map((id) =>
          getUnitById(id)
            .unwrap()
            .catch(() => null),
        ),
      );

      const validUnits = units.filter((unit) => unit !== null);

      const merged = billingMeters.map((meter) => {
        const unit = validUnits.find((u) => u.UnitId === meter.UnitId);
        return { ...meter, ...unit };
      });
      setMergedBillingMeters(merged);
      if (merged.length > 0) {
        setSelectedBillingMeter(merged[0]);
        fetchDataAndAggregate(merged[0]);
        const meterSavings = getMeterSavings(
          merged[0].BillingMeterId,
          energySavings,
        );
        setSelectedMeterSavings(meterSavings);
      }
    }
  };

  const handleTabChange = (newValue) => {
    setTabValue(newValue);
    if (newValue !== 0) {
      setSelectedBuilding(buildings[newValue - 1]);
      const unitIds = buildings[newValue - 1].BillingMeters.map(
        (meter) => meter.UnitId,
      );
      fetchUnits(unitIds, buildings[newValue - 1].BillingMeters);
    }
  };

  const handleBillingMeterChange = (event, newValue) => {
    if (!newValue) return;
    setSelectedBillingMeter(newValue);
    // setSelectedMeterSavings(getMeterSavings(newValue.BillingMeterId, energySavings));
    fetchDataAndAggregate(newValue);
  };

  useEffect(() => {
    if (selectedBillingMeter) {
      const meterSavings = getMeterSavings(
        selectedBillingMeter.BillingMeterId,
        energySavings,
      );
      setSelectedMeterSavings(meterSavings);
    }
  }, [selectedBillingMeter, energySavings]);

  useEffect(() => {
    setTabValue(0);
    setSelectedBillingMeter(null);
    setSelectedBuilding(null);
    setMergedBillingMeters(null);
    setSelectedMeterSavings(null);
    setBillingMeterData(null);
  }, [selectedAccount]);

  const getCurrentCO2Coefficient = (coefficientArray) => {
    const now = DateTime.now(); // Current date-time

    // Find the coefficient that is valid for the current date
    const validCoefficient = coefficientArray.find((item) => {
      const start = DateTime.fromISO(item.timestamp_start);
      const end = DateTime.fromISO(item.timestamp_end);
      return start <= now && end >= now; // Ensure now is within the start and end
    });

    // Return the value if found, otherwise null
    return validCoefficient ? validCoefficient.value : null;
  };

  const getCO2CoefficientForSelectedMeter = () => {
    if (!selectedBillingMeter || !selectedBuilding?.UserInput?.CO2Coefficients)
      return null;

    let coefficientArray = [];

    // Get the appropriate coefficient array based on the BillingMeterType
    switch (selectedBillingMeter.BillingMeterType) {
      case 'HeatMeter':
        coefficientArray =
          selectedBuilding.UserInput.CO2Coefficients.Heat || [];
        break;
      case 'GasMeter':
        coefficientArray = selectedBuilding.UserInput.CO2Coefficients.Gas || [];
        break;
      case 'ElectricityMeter':
        coefficientArray =
          selectedBuilding.UserInput.CO2Coefficients.Electricity || [];
        break;
      default:
        return null;
    }

    return getCurrentCO2Coefficient(coefficientArray);
  };

  const getDefaultCO2Coefficient = () => {
    switch (selectedBillingMeter?.BillingMeterType) {
      case 'ElectricityMeter':
        return 144.0; // Default for Electricity
      case 'HeatMeter':
        return 54.0; // Default for Heat
      case 'GasMeter':
        return 198.8; // Default for Gas
      default:
        return '-'; // If type is not recognized
    }
  };

  const validateCO2Entry = (array) => {
    const start = DateTime.fromISO(newStartTime);
    const end = DateTime.fromISO(newEndTime);

    if (!start || !end || !newValue) {
      setError('All fields are required.');
      return false;
    }

    if (start >= end) {
      setError('Start time must be before end time.');
      return false;
    }

    const isOverlapping = array.some((item) => {
      const existingStart = DateTime.fromISO(item.timestamp_start);
      const existingEnd = DateTime.fromISO(item.timestamp_end);
      return start <= existingEnd && end >= existingStart;
    });

    if (isOverlapping) {
      setError('The time period overlaps with an existing value.');
      return false;
    }

    setError('');
    return true;
  };

  const handleUpdateBuilding = async ({ buildingId, ...values }) => {
    try {
      const response = await updateBuilding({
        buildingId,
        building: values,
      }).unwrap();
      dispatch(
        showSnackbar({
          message: 'CO2 coefficient was updated',
          severity: 'success',
          response,
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error updating building',
          severity: 'error',
          response: error.message,
        }),
      );
      console.error('Error updating CO2 coefficient:', error);
    }
  };

  const handleUpdate = () => {
    let coefficientArray = [];

    if (selectedBillingMeter?.BillingMeterType === 'HeatMeter') {
      coefficientArray =
        selectedBuilding?.UserInput?.CO2Coefficients?.Heat || [];
    } else if (selectedBillingMeter?.BillingMeterType === 'GasMeter') {
      coefficientArray =
        selectedBuilding?.UserInput?.CO2Coefficients?.Gas || [];
    } else if (selectedBillingMeter?.BillingMeterType === 'ElectricityMeter') {
      coefficientArray =
        selectedBuilding?.UserInput?.CO2Coefficients?.Electricity || [];
    }

    if (validateCO2Entry(coefficientArray)) {
      // Create the new entry
      const newEntry = {
        timestamp_start: DateTime.fromISO(newStartTime).toFormat(
          "yyyy-MM-dd'T'HH:mm:ss'+00:00'",
        ),
        timestamp_end: DateTime.fromISO(newEndTime).toFormat(
          "yyyy-MM-dd'T'HH:mm:ss'+00:00'",
        ),
        value: parseFloat(newValue), // Ensure value is a number
      };

      // Construct the minimal payload for the update
      const updatedBuildingPayload = {
        Name: selectedBuilding.Name,
        Location: {
          Address: selectedBuilding.Location.Address,
          Latitude: selectedBuilding.Location.Latitude,
          Longitude: selectedBuilding.Location.Longitude,
        },
        BaselineDayInterval: selectedBuilding.BaselineDayInterval,
        BaselineDayOffset: selectedBuilding.BaselineDayOffset,
        UserInput: {
          CO2Coefficients: {
            ...selectedBuilding.UserInput.CO2Coefficients,
            [selectedBillingMeter.BillingMeterType === 'HeatMeter'
              ? 'Heat'
              : selectedBillingMeter.BillingMeterType === 'GasMeter'
                ? 'Gas'
                : 'Electricity']: [...coefficientArray, newEntry],
          },
        },
      };

      // Call the handleUpdateBuilding function
      handleUpdateBuilding({
        buildingId: selectedBuilding.BuildingId,
        ...updatedBuildingPayload,
      });

      // Close the popover after update
      handleClose();
    }
  };

  const handleRemove = () => {
    let coefficientArray = [];

    // Determine which array to target based on BillingMeterType
    if (selectedBillingMeter?.BillingMeterType === 'HeatMeter') {
      coefficientArray =
        selectedBuilding?.UserInput?.CO2Coefficients?.Heat || [];
    } else if (selectedBillingMeter?.BillingMeterType === 'GasMeter') {
      coefficientArray =
        selectedBuilding?.UserInput?.CO2Coefficients?.Gas || [];
    } else if (selectedBillingMeter?.BillingMeterType === 'ElectricityMeter') {
      coefficientArray =
        selectedBuilding?.UserInput?.CO2Coefficients?.Electricity || [];
    }

    // Find and remove the current valid coefficient
    const updatedCoefficientArray = coefficientArray.filter((item) => {
      const start = DateTime.fromISO(item.timestamp_start);
      const end = DateTime.fromISO(item.timestamp_end);
      return !(
        start <= DateTime.now() &&
        end >= DateTime.now() &&
        parseFloat(item.value) === parseFloat(newValue)
      );
    });

    // Construct the minimal payload for the update
    const updatedBuildingPayload = {
      Name: selectedBuilding.Name,
      Location: {
        Address: selectedBuilding.Location.Address,
        Latitude: selectedBuilding.Location.Latitude,
        Longitude: selectedBuilding.Location.Longitude,
      },
      BaselineDayInterval: selectedBuilding.BaselineDayInterval,
      BaselineDayOffset: selectedBuilding.BaselineDayOffset,
      UserInput: {
        CO2Coefficients: {
          ...selectedBuilding.UserInput.CO2Coefficients,
          [selectedBillingMeter.BillingMeterType === 'HeatMeter'
            ? 'Heat'
            : selectedBillingMeter.BillingMeterType === 'GasMeter'
              ? 'Gas'
              : 'Electricity']: updatedCoefficientArray,
        },
      },
    };

    // Call the handleUpdateBuilding function to remove the coefficient
    handleUpdateBuilding({
      buildingId: selectedBuilding.BuildingId,
      ...updatedBuildingPayload,
    });

    // Close the popover after removing
    handleClose();
  };

  return (
    <Container maxWidth="xxl" sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'start', p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ListItemText
            primary="Prædiktiv Varmeoptimering - Energibesparelse og CO2-reduktion"
            primaryTypographyProps={{
              variant: 'h5',
              color: 'text.primary',
              fontWeight: 700,
            }}
            secondary=" Se den samlede energibepsarelser og CO2-reduktion for bygningerne tilknyttet denne konto."
          />
        </Box>
      </Box>
      <Divider sx={{ mb: 4 }} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Tabs
          orientation="vertical"
          variant="scrollable"
          value={tabValue}
          onChange={(event, newValue) => handleTabChange(newValue)}
          aria-label="Vertical tabs example"
          sx={{
            minWidth: 250,
            maxWidth: 250,
            display: 'flex',
            mr: 10,
            justifyContent: 'left',
            '& .MuiTabs-indicator': {
              backgroundColor: 'hsl(63, 100%, 40%)',
              height: '4px',
              width: '4px',
              borderRadius: '5px',
            },
          }}
        >
          <Tab
            iconPosition="start"
            disableRipple
            label={
              <label style={{ cursor: 'pointer', fontWeight: 700 }}>
                Samlet overblik
              </label>
            }
            sx={{
              justifyContent: 'flex-start',
              textAlign: 'left',
              textTransform: 'none',
              '&.Mui-selected': {
                color: 'text.primary',
              },
            }}
          />
          {buildings &&
            buildings.map((building) => (
              <Tab
                key={building.BuildingId}
                iconPosition="start"
                disableRipple
                disabled={
                  !building.BillingMeters ||
                  building.BillingMeters.length === 0 ||
                  !building.JobSubscriptions.length > 0
                }
                label={
                  <label style={{ cursor: 'pointer', fontWeight: 400 }}>
                    {building.Name}
                  </label>
                }
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  textTransform: 'none',
                  '&.Mui-selected': {
                    color: 'text.primary',
                    fontWeight: 'bold',
                  },
                }}
              />
            ))}
        </Tabs>

        {/* Samlet overblik TabPanel */}
        <TabPanel value={tabValue} index={0}>
          <Suspense
            fallback={
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  verticalAlign: 'middle',
                  mt: 2,
                  height: 400,
                }}
              >
                <CircularProgress />
                <Typography variant="h6" sx={{ mt: 4 }}>
                  {loadingMessage}
                </Typography>
              </Box>
            }
          >
            <SavingsSummaryCards
              isLoading={isLoading || isLoadingData || isFetchingData}
              loadingMessage={loadingMessage}
              energySavings={energySavings || []}
              baselineDays={baselineDays}
              isTodayInSeason={isTodayInSeason || false}
              daysUntilNextSeason={daysUntilNextSeason}
            />
          </Suspense>
        </TabPanel>

        {buildings?.map((building, index) => (
          <TabPanel
            key={building.BuildingId}
            value={tabValue}
            index={index + 1}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                mb: 4,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  flex: 1,
                  width: '100%',
                }}
              >
                <ListItemText
                  primary={building.Name}
                  primaryTypographyProps={{
                    variant: 'h5',
                    color: 'text.primary',
                    fontWeight: 700,
                  }}
                  secondary={building.Location.Address}
                  sx={{ mb: 2 }}
                />
              </Box>
              {/* <Box sx={{ flex: 1 }}> */}
              <Autocomplete
                size="small"
                options={mergedBillingMeters || []}
                getOptionLabel={(option) =>
                  option.Name || `Meter ${option.BillingMeterId}`
                }
                value={selectedBillingMeter || null}
                onChange={handleBillingMeterChange}
                sx={{ width: 400, borderRadius: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    label="Måler"
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          {getIconForBillingMeterType(
                            selectedBillingMeter?.BillingMeterType,
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <InputAdornment position="start">
                      {getIconForBillingMeterType(option.BillingMeterType)}
                    </InputAdornment>
                    {option.Name || `Meter ${option.BillingMeterId}`}
                  </li>
                )}
              />
              {/* </Box> */}
            </Box>

            {building.BillingMeters && building.BillingMeters.length >= 1 ? (
              <>
                <Grid container spacing={2}>
                  {selectedMeterSavings &&
                    selectedMeterSavings?.TotalEnergyUsage !== 0 &&
                    baselineDays?.length >= 3 && (
                      <Grid item xs={12} md={12} lg={12} xl={5}>
                        {isLoading || isLoadingData || isFetchingData ? (
                          <Skeleton
                            variant="rounded"
                            width="100%"
                            height={400}
                          />
                        ) : (
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={12} lg={6}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  component="p"
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Gennemsnitlig Besparelse{' '}
                                  {baselineDays &&
                                    baselineDays.length <= 10 && (
                                      <Typography
                                        component="i"
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        *
                                      </Typography>
                                    )}
                                </Typography>
                                <Tooltip
                                  title="Besparelsesprocenten er baseret på forholdet mellem gennemsnitsforbruget på dage uden optimering (baseline) og det faktiske forbrug."
                                  placement="top"
                                >
                                  <InfoRoundedIcon
                                    size="small"
                                    sx={{
                                      color: (theme) =>
                                        theme.palette.mode === 'light'
                                          ? 'lightgray'
                                          : 'gray',
                                      ml: 1,
                                    }}
                                    fontSize="small"
                                  />
                                </Tooltip>
                              </Box>
                              <Typography component="p" variant="h5">
                                {selectedMeterSavings?.ActualEnergySavingsPercentage.toFixed(
                                  1,
                                )}{' '}
                                %
                              </Typography>
                            </Grid>

                            <Grid item xs={12} md={12} lg={6}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  component="p"
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  CO2e-koefficient anvendt
                                </Typography>
                                <Tooltip
                                  title="CO2e-koefficienten, der anvendes til at beregne CO2e-belastningen af varmen fra denne bygning vil være værdien, som I har angivet. Hvis der ikke er angivet en værdi, anvender vi en standardværdi."
                                  placement="bottom"
                                >
                                  <InfoRoundedIcon
                                    sx={{
                                      color: (theme) =>
                                        theme.palette.mode === 'light'
                                          ? 'lightgray'
                                          : 'gray',
                                      ml: 1,
                                    }}
                                    fontSize="small"
                                  />
                                </Tooltip>
                              </Box>

                              {getCO2CoefficientForSelectedMeter() !== null ? (
                                <Box sx={{ display: 'flex' }}>
                                  <Typography component="p" variant="h5">
                                    {getCO2CoefficientForSelectedMeter()} g/kWh
                                    <Tooltip
                                      title="CO2e-koefficienten, der anvendes til at beregne CO2e-belastningen af varmen fra denne bygning vil være værdien, som I har angivet. Hvis der ikke er angivet en værdi, anvender vi en standardværdi."
                                      placement="bottom"
                                    >
                                      <CheckCircleRoundedIcon
                                        fontSize="large"
                                        sx={{ color: 'success.main', ml: 1 }}
                                      />
                                    </Tooltip>
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    sx={{
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 1,
                                      ml: 1,
                                    }}
                                    onClick={handleClick}
                                  >
                                    <EditRoundedIcon size="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex' }}>
                                  <Typography component="p" variant="h5">
                                    {getDefaultCO2Coefficient()} g/kWh
                                    <HtmlTooltip
                                      title="Standardværdi anvendt."
                                      content={
                                        <>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            Der er ikke angivet en
                                            CO2e-koefficient for denne bygning.
                                            Standardværdien for
                                            CO2e-koefficienten for denne type
                                            måler er anvendt. For at angive en
                                            værdi klik på{' '}
                                            <EditRoundedIcon
                                              fontSize="small"
                                              sx={{ ml: 0.5, mt: 1 }}
                                            />
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                mt: 1,
                                              }}
                                            >
                                              <Box sx={{ display: 'flex' }}>
                                                <CheckCircleRoundedIcon
                                                  fontSize="small"
                                                  sx={{
                                                    color: 'success.main',
                                                    mr: 0.5,
                                                  }}
                                                />{' '}
                                                = koefficient fra
                                                forsyningsselskabet anvendt.
                                              </Box>

                                              <Box sx={{ display: 'flex' }}>
                                                <CheckCircleOutlineRoundedIcon
                                                  fontSize="small"
                                                  sx={{
                                                    color: 'text.secondary',
                                                    mr: 0.5,
                                                  }}
                                                />{' '}
                                                = standardværdi anvendt.
                                              </Box>
                                            </Box>
                                          </Typography>
                                        </>
                                      }
                                      placement="bottom"
                                    >
                                      <CheckCircleOutlineRoundedIcon
                                        sx={{ color: 'text.secondary', ml: 1 }}
                                      />
                                    </HtmlTooltip>
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    sx={{
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 1,
                                      ml: 1,
                                    }}
                                    onClick={handleClick}
                                  >
                                    <EditRoundedIcon size="small" />
                                  </IconButton>
                                </Box>
                              )}
                            </Grid>

                            <Grid item xs={12} md={12} lg={6}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  component="p"
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Aflæst forbrug
                                </Typography>
                                <Tooltip
                                  title="Forbruget er det aflæste forbrug direkte fra forsyningsselskabet, afregningsmåleren eller hovedmåleren for perioden."
                                  placement="top"
                                >
                                  <InfoRoundedIcon
                                    fontSize="small"
                                    sx={{
                                      color: (theme) =>
                                        theme.palette.mode === 'light'
                                          ? 'lightgray'
                                          : 'gray',
                                      ml: 1,
                                    }}
                                  />
                                </Tooltip>
                              </Box>
                              <Typography component="p" variant="h5">
                                {formatEnergyUnit(
                                  selectedMeterSavings?.TotalEnergyUsage -
                                    selectedMeterSavings?.PausedEnergyUsage,
                                )}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} md={12} lg={6}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  component="p"
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Samlet CO2e belastning fra varmeforbruget.
                                </Typography>
                                <Tooltip
                                  title="CO2e belastning fra det samlede varmeforbrug er beregnet vha. CO2e-koefficienten ovenfor og det aflæste forbrug i perioden."
                                  placement="top"
                                >
                                  <InfoRoundedIcon
                                    fontSize="small"
                                    sx={{
                                      color: (theme) =>
                                        theme.palette.mode === 'light'
                                          ? 'lightgray'
                                          : 'gray',
                                      ml: 1,
                                    }}
                                  />
                                </Tooltip>
                              </Box>
                              <Typography component="p" variant="h5">
                                {formatCO2Unit(
                                  selectedMeterSavings?.CO2Savings
                                    ?.TotalCO2Emissions,
                                )}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} md={12} lg={6}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  component="p"
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Estimeret forbrug uden optimering
                                </Typography>
                                <Tooltip
                                  title="For at estimere hvad forbruget havde været i hele perioden uden optimering, anvendes gennemsnittet af forbruget på baseline-dage. Dette bruges i beregningen af forskellen mellem baseline-dage og dage hvor Prædiktiv Varmeoptimering er aktiv. "
                                  placement="top"
                                >
                                  <InfoRoundedIcon
                                    fontSize="small"
                                    sx={{
                                      color: (theme) =>
                                        theme.palette.mode === 'light'
                                          ? 'lightgray'
                                          : 'gray',
                                      ml: 1,
                                    }}
                                  />
                                </Tooltip>
                              </Box>
                              <Typography component="p" variant="h5">
                                {formatEnergyUnit(
                                  selectedMeterSavings?.EstimatedEnergyUsageWithoutOptimization,
                                )}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} md={12} lg={6}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  component="p"
                                  variant="body2"
                                  color="text.secondary"
                                  gutterBottom
                                >
                                  CO2e-besparelse
                                </Typography>
                                <Tooltip
                                  title="CO2e besparelsen som er opnået med Prædiktiv Varmeoptimering aktiv. Den samledee CO2e-belastning ovenfor havde altså være højere, hvis ikke optimeringen havde været aktiv."
                                  placement="top"
                                >
                                  <InfoRoundedIcon
                                    fontSize="small"
                                    sx={{
                                      color: (theme) =>
                                        theme.palette.mode === 'light'
                                          ? 'lightgray'
                                          : 'gray',
                                      ml: 1,
                                    }}
                                  />
                                </Tooltip>
                              </Box>
                              <Typography component="p" variant="h5">
                                {formatCO2Unit(
                                  selectedMeterSavings?.CO2Savings
                                    ?.TotalCO2Savings,
                                )}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} md={12} lg={6}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  component="p"
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Besparelse i perioden
                                </Typography>
                                <Tooltip
                                  title="Forskellen mellem det beregnede forbrug uden Prædiktiv Varmeoptimering og det aflæste forbrug i perioden."
                                  placement="top"
                                >
                                  <InfoRoundedIcon
                                    fontSize="small"
                                    sx={{
                                      color: (theme) =>
                                        theme.palette.mode === 'light'
                                          ? 'lightgray'
                                          : 'gray',
                                      ml: 1,
                                    }}
                                  />
                                </Tooltip>
                              </Box>
                              <Typography component="p" variant="h5">
                                {formatEnergyUnit(
                                  selectedMeterSavings?.TotalEnergySavings,
                                )}
                              </Typography>
                            </Grid>
                          </Grid>
                        )}
                        <br></br>
                        {baselineDays &&
                          baselineDays.length <= 10 &&
                          baselineDays.length !== 0 && (
                            <Typography
                              component="i"
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 2 }}
                            >
                              * Der har været under 10 baseline-dage, og
                              beregningen af besparelsen kan derfor fortsat have
                              store udsving.
                            </Typography>
                          )}
                      </Grid>
                    )}

                  <Grid item xs={12} md={12} lg={12} xl={7}>
                    <Suspense
                      fallback={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '80vh',
                          }}
                        >
                          <CircularProgress />
                          <Typography variant="h6" sx={{ mt: 4 }}>
                            {loadingMessage}
                          </Typography>
                        </Box>
                      }
                    >
                      <HorizontalBarChart
                        loading={isLoading || isLoadingData || isFetchingData}
                        averageEnergyUsageBaselineDay={
                          selectedMeterSavings?.AverageEnergyUsageBaselineDay
                        }
                        averageEnergyUsageControlDay={
                          selectedMeterSavings?.AverageEnergyUsageControlDay
                        }
                        baseUnit={selectedMeterSavings?.BaseUnit}
                      />
                    </Suspense>
                  </Grid>

                  <Grid item xs={12} md={12} lg={12} xl={12}>
                    <Suspense
                      fallback={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '80vh',
                          }}
                        >
                          <CircularProgress />
                          <Typography variant="h6" sx={{ mt: 4 }}>
                            {loadingMessage}
                          </Typography>
                        </Box>
                      }
                    >
                      <EnergyConsumptionChart
                        data={billingMeterData}
                        controlPeriods={controlPeriods}
                        baselineDays={baselineDays}
                        seasonStart={start}
                        seasonEnd={end}
                        loading={isLoading || isLoadingData || isFetchingData}
                        loadingMessage={loadingMessage}
                        energySavings={energySavings?.EnergySavingsSummary} // Adjust to fit the new structure
                      />
                    </Suspense>
                  </Grid>
                </Grid>
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '80vh',
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  No billing meters found for {building.Name}. Start{' '}
                  <Link to="/buildings"> here</Link>.
                </Typography>
              </Box>
            )}
          </TabPanel>
        ))}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box
          sx={{
            p: 2,
            width: '500px',
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? 'hsl(0, 0%, 100%)' : '#121212',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">CO2e-koefficient</Typography>
            <Tooltip
              title="CO2e-koefficienten, der anvendes til at beregne CO2e-belastningen af varmen fra denne bygning vil være værdien, som I har angivet. Hvis der ikke er angivet en værdi, anvender vi en standardværdi."
              placement="top"
            >
              <InfoRoundedIcon
                fontSize="small"
                sx={{
                  color: (theme) =>
                    theme.palette.mode === 'light' ? 'lightgray' : 'gray',
                  ml: 1,
                }}
              />
            </Tooltip>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="CO2e-koefficient"
                variant="outlined"
                type="number"
                fullWidth
                inputProps={{ step: 0.01, min: 0 }}
                value={newValue}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  setNewValue(value < 0 ? 0 : value);
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Start"
                type="datetime-local"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                value={newStartTime}
                onChange={(e) => setNewStartTime(e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Slut"
                type="datetime-local"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                value={newEndTime}
                onChange={(e) => setNewEndTime(e.target.value)}
              />
            </Grid>
          </Grid>

          <DialogActions sx={{ mt: 2 }}>
            {getCO2CoefficientForSelectedMeter() !== null ? (
              // Case when a valid coefficient exists - show 'Fjern' and 'Afbryd' buttons
              <>
                <Button onClick={handleClose} variant="text" size="small">
                  Afbryd
                </Button>
                <Button
                  onClick={handleRemove}
                  variant="contained"
                  color="error"
                >
                  Fjern
                </Button>
              </>
            ) : (
              // Case when no valid coefficient exists - show 'Tilføj' button
              <Button onClick={handleUpdate} variant="contained">
                Tilføj
              </Button>
            )}
          </DialogActions>
        </Box>
      </Popover>
    </Container>
  );
};

export default PortalPage;
